import { useServerTime } from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { RxDocument } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { v4 as uuidv4 } from "uuid";
import type {
  EpocusBilling,
  ProcedureCode,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-billing";
import { useRxCollectionEpocusBilling } from "../../../../../../global-providers/rxdb-provider/collections/epocus-billing";
import type { EpocusEncounter } from "../../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import {
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import type {
  EpocusExam,
  NonBillableReason,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam";
import {
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  useRxCollectionEpocusExam,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam";
import {
  type ExamWorksheetData,
  isMultiIndicationWorksheet,
  isProcedureWorksheet,
  isUsWorksheet,
  type MskRegion,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { EpocusProgram } from "../../../../../../global-providers/rxdb-provider/collections/epocus-program";
import { useUser } from "../../../../../../global-providers/user-provider/provider";
import {
  type BillingLine,
  billingLinesFor,
  otherBillingLines,
} from "../../../../_lib/billing/billing-lines";
import {
  billingModifierFor,
  billingRecordSettingFor,
} from "../../../../_lib/billing/billing-modifier";
import { BillingSection } from "../../../../_lib/billing/billing-section";
import {
  BillingStatusNote,
  useExamBillingStatus,
} from "../../../../_lib/billing/billing-status-note";
import { CPT_DESCRIPTIONS } from "../../../../_lib/billing/cpt-descriptions";
import {
  suggestDiagnosisCodes,
  suggestMskDiagnosisCodes,
  suggestMskRegionCodes,
  toDocumentationStatus,
  validateDocumentation,
} from "../../../../_lib/billing/epocus-billing-engine";
import { NonBillableControl } from "../../../../_lib/billing/non-billable-control";
import { useBillingCodesEnabled } from "../../../../_lib/billing/use-billing-codes-enabled";
import { isPresetConflict } from "../../../../_lib/dicom-preset-mapping";
import {
  EXAM_ICONS,
  EXAM_LABELS,
} from "../../../../_lib/epocus-exam-constants";
import { AlertTriangle } from "../../../../_lib/epocus-icons";
import { EpocusImageGallery } from "../../../../_lib/epocus-image-gallery";
import {
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_RED,
  EPOCUS_RED_BG,
  EPOCUS_RED_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_IMPORTED_BG,
  EPOCUS_STATUS_IMPORTED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_TO_DOC_BORDER,
  rem,
} from "../../../../_lib/epocus-palette";
import { epocusTheme } from "../../../../_lib/epocus-theme-provider";
import {
  collectAssignedExamImageIds,
  imagingInstanceToExamImage,
  useAssignedExamImageIds,
  useEncounterImagingInstances,
} from "../../../../_lib/hooks/use-encounter-imaging-instances";
import { useEpocusExamImages } from "../../../../_lib/hooks/use-epocus-exam-images";
import { RoleGate } from "../../../../_lib/rbac/role-gate";
import { useCan } from "../../../../_lib/rbac/use-can";
import { ReviewOutcomeCard } from "../../../../_lib/review-outcome-card";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../../../_lib/use-active-program";
import { useAllNormal } from "../../../../_lib/use-all-normal";
import { useEnabledExamTypes } from "../../../../_lib/use-enabled-exam-types";
import { useExamReview } from "../../../../_lib/use-exam-review";
import { AortaWorksheet } from "../../../../_lib/worksheets/aorta-worksheet";
import { BladderWorksheet } from "../../../../_lib/worksheets/bladder-worksheet";
import { CardiacWorksheet } from "../../../../_lib/worksheets/cardiac-worksheet";
import { EfastWorksheet } from "../../../../_lib/worksheets/efast-worksheet";
import { LungWorksheet } from "../../../../_lib/worksheets/lung-worksheet";
import { untaggedMskImages } from "../../../../_lib/worksheets/msk-regions";
import {
  type MskRegionCode,
  MskWorksheet,
} from "../../../../_lib/worksheets/msk-worksheet";
import { PregnancyWorksheet } from "../../../../_lib/worksheets/pregnancy-worksheet";
import { mskToGuidedJointInjection } from "../../../../_lib/worksheets/procedures/msk-to-guided-joint-injection";
import { ProcedureWorksheet } from "../../../../_lib/worksheets/procedures/procedure-worksheet";
import { RenalWorksheet } from "../../../../_lib/worksheets/renal-worksheet";
import { SigningChecklist } from "../../../../_lib/worksheets/signing-checklist";
import {
  isExamComplete,
  missingForSigning,
} from "../../../../_lib/worksheets/signing-gate";
import { usDefinitionFor } from "../../../../_lib/worksheets/us/us-definitions";
import { UsWorksheet } from "../../../../_lib/worksheets/us/us-worksheet";
import { useWorksheetAutoSave } from "../../../../_lib/worksheets/use-worksheet-auto-save";
import { WorksheetActions } from "../../../../_lib/worksheets/worksheet-primitives";
import { linkToEpocusEncounter } from "../../../../links";
import {
  isLastExamToSubmit,
  submitEncounterForReview,
} from "../../_lib/encounter-submission";
import { AmendReportPanel } from "./_lib/amend-report-panel";
import {
  type AmendmentKind,
  appendAmendment,
  reportVersions,
} from "./_lib/report-amendment";
import { ReportHistory } from "./_lib/report-history";
import { SwitchToGuidedProcedureDialog } from "./_lib/switch-to-guided-procedure-dialog";

const NON_BILLABLE_EXPORTED =
  "This study has been exported to the biller, so whether it is billable can no longer be changed here.";

const BILLING_NOT_SET_UP =
  "This scan's program hasn't synced to this device, so its billing setting is unknown. Try again once it has.";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 960,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const PerformerLockNotice = styled(Typography)({
  backgroundColor: EPOCUS_STATUS_IMPORTED_BG,
  color: EPOCUS_STATUS_IMPORTED_TEXT,
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: rem(13),
  fontWeight: 600,
});

// Deep-clone exam worksheet data bypassing the RxDB Proxy.
// In dev/acceptance environments RxDB v15 wraps document fields in a Proxy
// that violates V8's invariants when stringified. Reading from _data directly
// (the frozen plain object) avoids the Proxy entirely.
const cloneWorksheetData = (exam: RxDocument<EpocusExam>): ExamWorksheetData =>
  JSON.parse(JSON.stringify(exam._data.worksheetData)) as ExamWorksheetData;

export const EpocusExamPage: FC = () => {
  const { encounterId, examId } = useParams<{
    encounterId: string;
    examId: string;
  }>();
  const navigate = useNavigate();
  const examCollection = useRxCollectionEpocusExam();
  const billingCollection = useRxCollectionEpocusBilling();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const user = useUser();
  const { getCurrentTime } = useServerTime();

  const billingCodesEnabled = useBillingCodesEnabled();

  const { result: exams, isFetching } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: { id: examId ?? "", encounterId: encounterId ?? "" },
      }),
  );
  const exam = exams?.[0] ?? null;

  const { review } = useExamReview(exam?.id);
  const signedReview = review?.signedAt != null ? review : null;

  const { result: encounterExams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      collection.find({ selector: { encounterId: encounterId ?? "" } }),
  );

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) => collection.findOne(encounterId ?? ""),
  );
  const encounter = encounters?.[0] ?? null;

  // Track existing billing record for this exam (for locked state + idempotency)
  const [existingBilling, setExistingBilling] = useState<EpocusBilling | null>(
    null,
  );
  useEffect(() => {
    if (!billingCollection || !examId) return;
    const sub = billingCollection
      .findOne({ selector: { examId } })
      .$.subscribe((doc) => {
        setExistingBilling((doc?.toJSON() as EpocusBilling) ?? null);
      });
    return () => sub.unsubscribe();
  }, [billingCollection, examId]);

  // Stable reference prevents useEpocusExamImages from firing on every render
  // when exam is null (loading), which would cause an infinite re-render loop.
  // While an amendment is open its image edits are held here, and written
  // with the amendment rather than on their own.
  const [amendImages, setAmendImages] = useState<EpocusExam["images"] | null>(
    null,
  );
  const examImages = useMemo(
    () => amendImages ?? exam?.images ?? [],
    [exam, amendImages],
  );
  const hydratedImages = useEpocusExamImages(examImages);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // PNE-212: Load ImagingInstances for this encounter and auto-import any that
  // haven't yet been added to exam.images (handles DICOM arriving after exam creation).
  const { instances: imagingInstances, dominantPreset } =
    useEncounterImagingInstances(encounter?.accessionNumber);
  const { assignedImageIds, isFetching: assignedFetching } =
    useAssignedExamImageIds(encounterId);
  useEffect(() => {
    if (!examCollection || !exam || imagingInstances.length === 0) return;
    // Only the clinician who signed a report takes late images onto it, and
    // not while an amendment is open.
    if (exam.signedAt && (exam.signedByUserId !== user.id || amendImages))
      return;
    // An unsettled claim query looks like "nothing is claimed", which would
    // re-import a sibling's images.
    if (assignedFetching) return;
    let isActive = true;
    const newInstances = imagingInstances.filter(
      (inst) => !assignedImageIds.has(inst.id),
    );
    if (newInstances.length === 0) return;
    const newImages = newInstances.map(imagingInstanceToExamImage);
    const importImages = async () => {
      try {
        const doc = await examCollection.findOne(exam.id).exec();
        if (!isActive || !doc) return;
        // Re-read every exam in the encounter, not just this one: an instance
        // claimed by a sibling between effect setup and here must not be
        // claimed again, or the image ends up on two exams.
        const siblings = await examCollection
          .find({ selector: { encounterId: exam.encounterId } })
          .exec();
        const claimedIds = collectAssignedExamImageIds(
          siblings.map((sibling) => sibling._data as EpocusExam),
        );
        const stillNew = newImages.filter((img) => !claimedIds.has(img.id));
        if (stillNew.length === 0) return;
        const now = getCurrentTime().toISOString();
        await doc.incrementalModify((data) => ({
          ...data,
          images: [...data.images, ...stillNew],
          meta: { ...data.meta, updatedAt: now, updatedByUserId: user.id },
        }));
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to import imaging instances into exam", error);
      }
    };
    void importImages();
    return () => {
      isActive = false;
    };
  }, [
    imagingInstances,
    assignedImageIds,
    assignedFetching,
    exam,
    examCollection,
    getCurrentTime,
    user.id,
    amendImages,
  ]);

  // PNE-212: Block submission when the dominant DICOM preset conflicts with the exam type.
  // Uses the dominant (most common) preset to avoid false positives from stray images.
  const conflictingPreset = useMemo(() => {
    if (!exam || !dominantPreset) return null;
    return isPresetConflict(dominantPreset, exam.examType)
      ? dominantPreset
      : null;
  }, [dominantPreset, exam]);

  const [worksheetData, setWorksheetData] = useState<ExamWorksheetData | null>(
    null,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tickState, setTickState] = useState<{
    scope: string;
    overrides: Record<string, boolean>;
  }>({ scope: "", overrides: {} });

  const { programs } = useMyPrograms();
  const program = encounter?.programId
    ? (programs.find((candidate) => candidate.id === encounter.programId) ??
      null)
    : selectActiveProgram(programs);
  const modifierFor = useCallback(
    (cpt: string) => (program ? billingModifierFor(program, cpt) : null),
    [program],
  );

  // Stable plain-object copy of the persisted worksheet data, recomputed only
  // when the exam identity changes. We cannot fall back to exam.worksheetData
  // directly — in dev/acceptance environments RxDB v15 returns a Proxy for
  // nested objects, which violates V8's non-configurable property invariant
  // when JSON.stringify'd inside the auto-save cleanup flush.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on exam identity; exam._data is intentionally omitted so we don't re-snapshot over in-progress user edits
  const persistedData = useMemo<ExamWorksheetData | null>(
    () => (exam ? cloneWorksheetData(exam) : null),
    [exam?.id],
  );

  // Use the user's local edits when available; otherwise fall back to the
  // persisted plain-object snapshot. This means currentData is non-null on the
  // same render that exam loads, eliminating the extra "Exam not found" flash
  // that occurred when exam was available but worksheetData hadn't been seeded.
  // A signed report shows what is stored, so a refused amendment cannot linger
  // on screen; an amendment seeds worksheetData when it opens.
  const storedData = useMemo<ExamWorksheetData | null>(
    () => (exam?.status === "submitted" ? cloneWorksheetData(exam) : null),
    [exam],
  );
  const currentData = worksheetData ?? storedData ?? persistedData;

  const handleWorksheetChange = useCallback((data: ExamWorksheetData) => {
    setWorksheetData(data);
  }, []);

  const isSubmitted = exam?.status === "submitted";

  // Reviewers and program oversight may open an exam to read it, but must not
  // edit it (PNE-533). `readOnly` covers both reasons the worksheet locks —
  // already submitted, or this role can't author — and gates auto-save too, so
  // merely viewing an exam never writes to it.
  const canEditExam = useCan("editOwnExam");

  // An encounter with no performer is still unclaimed, and documenting it is
  // what claims it — so only a performer already set to somebody else locks the
  // worksheet. The backend performer-lock rejects the write regardless; this
  // stops the user filling in a form whose submission can never land.
  const lockedToOtherPerformer =
    !!encounter?.performerId && encounter.performerId !== user.id;

  // Only the clinician who signed the report may amend or confirm it; the
  // server refuses anyone else's change to a signed exam.
  const canAmend =
    billingCodesEnabled &&
    !!exam?.signedAt &&
    exam.signedByUserId === user.id &&
    canEditExam;
  const [amendMode, setAmendMode] = useState<AmendmentKind | null>(null);
  const amending = canAmend && amendMode === "AMEND";

  const readOnly =
    (isSubmitted && !amending) ||
    isSubmitting ||
    !canEditExam ||
    lockedToOtherPerformer;

  // Never auto-saved once submitted: an amendment is written whole when signed.
  const { cancel: cancelAutoSave } = useWorksheetAutoSave(
    examCollection,
    exam?.id ?? null,
    currentData,
    readOnly || isSubmitted,
  );

  const versions = useMemo(() => (exam ? reportVersions(exam) : []), [exam]);
  const [viewingVersion, setViewingVersion] = useState("current");
  const viewedVersion = versions.find(
    (version) => version.key === viewingVersion && version.key !== "current",
  );

  const { isAllNormal, toggle: toggleAllNormal } = useAllNormal({
    currentData,
    onSetNormal: (normalData) => setWorksheetData(normalData),
    onClear: (emptyData) => setWorksheetData(emptyData),
  });

  const isComplete = currentData
    ? isExamComplete(currentData, examImages)
    : false;

  const enabledExamTypes = useEnabledExamTypes();
  const [guidedSwitchRegion, setGuidedSwitchRegion] =
    useState<MskRegion | null>(null);
  const [switchingWorksheet, setSwitchingWorksheet] = useState(false);

  const billingLines = useMemo(
    () => (currentData ? billingLinesFor(currentData) : []),
    [currentData],
  );
  const otherLines = useMemo(
    () =>
      currentData && !isUsWorksheet(currentData)
        ? otherBillingLines(billingLines)
        : [],
    [currentData, billingLines],
  );
  const tickScope = `${exam?.id}:${currentData?.examType}`;
  const tickOverrides =
    tickState.scope === tickScope ? tickState.overrides : {};
  const tickedByKey = Object.fromEntries(
    [...billingLines, ...otherLines].map((line) => [
      line.key,
      tickOverrides[line.key] ?? line.preTicked,
    ]),
  );
  const tickedLines = [...billingLines, ...otherLines].filter(
    (line) => tickedByKey[line.key],
  );

  const billingStatus = useExamBillingStatus(exam?.id);
  const nonBillable = exam?.nonBillable ?? false;
  const signingGaps =
    billingCodesEnabled && currentData && !isSubmitted
      ? missingForSigning({
          data: currentData,
          images: examImages,
          hasTickedCode: tickedLines.length > 0,
          nonBillable,
        })
      : [];

  const definitionCodes =
    currentData && isUsWorksheet(currentData)
      ? (usDefinitionFor(currentData.examType)?.billing.codes ?? [])
      : [];
  const signedLines: BillingLine[] = (
    existingBilling?.procedureCodes ?? []
  ).map((code, index) => ({
    key: `${index}`,
    cpt: code.cpt,
    label:
      definitionCodes.find((definitionCode) => definitionCode.cpt === code.cpt)
        ?.label ??
      CPT_DESCRIPTIONS[code.cpt] ??
      code.cpt,
    sideModifier: code.sideModifier ?? null,
    units: code.units,
    preTicked: true,
    notes: [],
    modifier: code.modifier,
  }));

  const mskRegionCodes = useMemo(() => {
    if (!billingCodesEnabled || currentData?.examType !== "msk")
      return undefined;
    const suggestions = suggestMskRegionCodes(currentData, null);
    return Object.fromEntries(
      Object.entries(suggestions).map(([region, suggestion]) => [
        region,
        {
          code: suggestion.procedureCode.cpt,
          modifier: modifierFor(suggestion.procedureCode.cpt),
        },
      ]),
    ) as Partial<Record<MskRegion, MskRegionCode>>;
  }, [billingCodesEnabled, currentData, modifierFor]);

  const untaggedMskImageCount =
    exam?.examType === "msk" ? untaggedMskImages(examImages).length : 0;

  const handleTagMskImages = async (
    imageIds: string[],
    region: MskRegion | null,
  ) => {
    if (!examCollection || !exam) return;
    if (amendImages) {
      const ids = new Set(imageIds);
      setAmendImages(
        amendImages.map((image) =>
          ids.has(image.id) ? { ...image, mskRegion: region } : image,
        ),
      );
      return;
    }
    const doc = await examCollection.findOne(exam.id).exec();
    if (!doc) return;
    const ids = new Set(imageIds);
    const now = getCurrentTime().toISOString();
    await doc.incrementalModify((docData) => ({
      ...docData,
      images: docData.images.map((image) =>
        ids.has(image.id) ? { ...image, mskRegion: region } : image,
      ),
      meta: { ...docData.meta, updatedAt: now, updatedByUserId: user.id },
    }));
  };

  const handleToggleNeedleInTarget = async (
    imageId: string,
    needleInTarget: boolean,
  ) => {
    if (!examCollection || !exam) return;
    if (amendImages) {
      setAmendImages(
        amendImages.map((image) =>
          image.id === imageId ? { ...image, needleInTarget } : image,
        ),
      );
      return;
    }
    const doc = await examCollection.findOne(exam.id).exec();
    if (!doc) return;
    const now = getCurrentTime().toISOString();
    await doc.incrementalModify((docData) => ({
      ...docData,
      images: docData.images.map((image) =>
        image.id === imageId ? { ...image, needleInTarget } : image,
      ),
      meta: { ...docData.meta, updatedAt: now, updatedByUserId: user.id },
    }));
  };

  const handleSwitchToGuidedJointInjection = async () => {
    if (
      !examCollection ||
      !exam ||
      guidedSwitchRegion === null ||
      currentData?.examType !== "msk"
    )
      return;
    setSwitchingWorksheet(true);
    try {
      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }
      cancelAutoSave();
      const procedureData = mskToGuidedJointInjection(
        currentData,
        guidedSwitchRegion,
      );
      const now = getCurrentTime().toISOString();
      await doc.incrementalModify((docData) => ({
        ...docData,
        examType: procedureData.examType,
        worksheetData: procedureData,
        meta: { ...docData.meta, updatedAt: now, updatedByUserId: user.id },
      }));
      setWorksheetData(procedureData);
      setGuidedSwitchRegion(null);
    } catch {
      toast.error("Couldn't switch the worksheet. Please try again.");
    } finally {
      setSwitchingWorksheet(false);
    }
  };

  const handleNonBillableChange = async (reason: NonBillableReason | null) => {
    if (!examCollection || !exam) return;
    const doc = await examCollection.findOne(exam.id).exec();
    if (!doc) return;
    const now = getCurrentTime().toISOString();
    await doc.incrementalModify((docData) => ({
      ...docData,
      nonBillable: reason !== null,
      nonBillableReason: reason,
      meta: { ...docData.meta, updatedAt: now, updatedByUserId: user.id },
    }));
  };

  const handleStartAmendment = (mode: AmendmentKind) => {
    if (!exam) return;
    setViewingVersion("current");
    if (mode === "AMEND") {
      setWorksheetData(cloneWorksheetData(exam));
      setAmendImages(JSON.parse(JSON.stringify(exam._data.images)));
    }
    setAmendMode(mode);
  };

  const closeAmendment = () => {
    setWorksheetData(null);
    setAmendImages(null);
    setAmendMode(null);
  };

  const handleSignAmendment = async (reason: string) => {
    if (!examCollection || !exam || !currentData || !amendMode || isSubmitting)
      return;
    const kind = amendMode;
    setIsSubmitting(true);
    try {
      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }
      const now = getCurrentTime().toISOString();
      const id = uuidv4();
      await doc.incrementalModify((data) =>
        appendAmendment({
          exam: data,
          kind,
          reason,
          worksheet: currentData,
          images: amendImages,
          id,
          now,
          userId: user.id,
          reviewId: signedReview?.id ?? null,
        }),
      );
      closeAmendment();
      toast.success(
        kind === "AMEND"
          ? "Amendment signed."
          : "Report confirmed as it stands.",
      );
    } catch {
      toast.error("Failed to sign. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!examCollection || !exam || !currentData) return;
    const doc = await examCollection.findOne(exam.id).exec();
    if (!doc) {
      toast.error("Could not save — exam not found");
      return;
    }
    const now = getCurrentTime().toISOString();
    await doc.incrementalModify((data) => ({
      ...data,
      worksheetData: currentData,
      meta: { ...data.meta, updatedAt: now, updatedByUserId: user.id },
    }));
    toast.success("Draft saved");
  };

  const advanceEncounterIfLastExam = async () => {
    if (!exam || !encounter) return;
    if (!isLastExamToSubmit(encounterExams, exam.id)) return;
    try {
      await submitEncounterForReview(
        encounterCollection,
        encounter.id,
        user.id,
        getCurrentTime().toISOString(),
      );
    } catch (err) {
      console.error("Submit encounter for review failed:", err);
    }
  };

  // Only called when epocus.billing-codes is off — see handleSubmit below.
  const handleSubmitWithoutBilling = async () => {
    if (!examCollection || !exam || !currentData || !encounter || isSubmitting)
      return;

    setIsSubmitting(true);
    try {
      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }

      cancelAutoSave();
      const now = getCurrentTime().toISOString();
      await doc.incrementalModify((data) => ({
        ...data,
        worksheetData: currentData,
        status: "submitted",
        meta: { ...data.meta, updatedAt: now, updatedByUserId: user.id },
      }));

      await advanceEncounterIfLastExam();

      navigate(linkToEpocusEncounter(encounter.id));
    } catch {
      toast.error("Failed to submit exam. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!exam || !currentData) return;

    const hasIndication = isMultiIndicationWorksheet(currentData)
      ? currentData.indication.length > 0
      : !!currentData.indication;
    if (!hasIndication) {
      toast.error("Please select a clinical indication before submitting.");
      return;
    }

    if ((exam.images?.length ?? 0) === 0) {
      toast.error(
        "Images required — add at least one image or clip before submitting.",
      );
      return;
    }

    if (untaggedMskImageCount > 0) {
      toast.error("Tag every image to the region it shows before submitting.");
      return;
    }

    if (!billingCodesEnabled) {
      await handleSubmitWithoutBilling();
      return;
    }

    if (!program) {
      toast.error(BILLING_NOT_SET_UP);
      return;
    }

    if (signingGaps.length > 0) {
      toast.error("Finish the items listed by the Sign button first.");
      return;
    }

    const diagCodes = isMultiIndicationWorksheet(currentData)
      ? []
      : currentData.examType === "msk"
        ? suggestMskDiagnosisCodes(currentData)
        : suggestDiagnosisCodes(currentData.indication);
    if (
      diagCodes.length === 0 &&
      !nonBillable &&
      !isMultiIndicationWorksheet(currentData)
    ) {
      toast.error(
        "No ICD-10 code found for the selected indication. Please select a different indication.",
      );
      return;
    }

    await handleSign(program, diagCodes);
  };

  const handleSign = async (
    signingProgram: EpocusProgram,
    diagnosisCodes: EpocusBilling["diagnosisCodes"],
  ) => {
    if (
      !examCollection ||
      !billingCollection ||
      !exam ||
      !currentData ||
      !encounter ||
      isSubmitting
    )
      return;

    const performerId = encounter.performerId ?? user.id;
    setIsSubmitting(true);

    try {
      const existing = await billingCollection
        .findOne({ selector: { examId: exam.id } })
        .exec();
      if (existing?.locked) {
        toast.error("Billing record is locked and cannot be modified.");
        return;
      }

      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }

      cancelAutoSave();

      const now = getCurrentTime().toISOString();

      const validation = validateDocumentation({
        indication: isMultiIndicationWorksheet(currentData)
          ? (currentData.indication[0] ?? null)
          : currentData.indication,
        imagesStored: (exam.images?.length ?? 0) > 0,
        findingsPopulated: isComplete,
      });

      const procedureCodes: ProcedureCode[] = tickedLines.map((line) => ({
        cpt: line.cpt,
        modifier: billingModifierFor(signingProgram, line.cpt),
        sideModifier: line.sideModifier,
        units: line.units,
        systemSuggested: line.preTicked,
        performerConfirmed: true,
        overrideReason: null,
      }));

      const existingData = existing?.toJSON() as EpocusBilling | undefined;
      const billingRecord: EpocusBilling = {
        id: existingData?.id ?? uuidv4(),
        examId: exam.id,
        encounterId: encounter.id,
        programId: signingProgram.id,
        renderingProviderUserId: user.id,
        dateOfService: encounter.date,
        patientMrn: encounter.patientMrn ?? null,
        patientDob: encounter.patientDob ?? existingData?.patientDob ?? null,
        payerName: existingData?.payerName ?? null,
        policyNumber: existingData?.policyNumber ?? null,
        performingPhysicianName: existingData?.performingPhysicianName ?? null,
        performingPhysicianNpi: existingData?.performingPhysicianNpi ?? null,
        supervisingPhysicianNpi: existingData?.supervisingPhysicianNpi ?? null,
        facilityNpi: existingData?.facilityNpi ?? null,
        ...billingRecordSettingFor(signingProgram, procedureCodes),
        procedureCodes,
        diagnosisCodes,
        documentationStatus: toDocumentationStatus(validation.status),
        imagesStored: (exam.images?.length ?? 0) > 0,
        exportedAt: existingData?.exportedAt ?? null,
        locked: existingData?.locked ?? false,
        meta: {
          createdAt: existingData?.meta?.createdAt ?? now,
          updatedAt: now,
          createdByUserId: existingData?.meta?.createdByUserId ?? performerId,
          updatedByUserId: performerId,
        },
      };

      await billingCollection.upsert(billingRecord);

      await doc.incrementalModify((data) => ({
        ...data,
        worksheetData: currentData,
        status: "submitted",
        signedAt: now,
        signedByUserId: user.id,
        meta: { ...data.meta, updatedAt: now, updatedByUserId: user.id },
      }));

      await advanceEncounterIfLastExam();

      navigate(linkToEpocusEncounter(encounter.id));
    } catch {
      toast.error("Failed to sign. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!exam || !currentData) {
    return (
      <PageRoot>
        <Typography>Exam not found.</Typography>
      </PageRoot>
    );
  }

  const imageGallery = (
    <>
      {conflictingPreset && (
        <Box
          role="alert"
          style={{
            backgroundColor: EPOCUS_RED_BG,
            border: `1px solid ${EPOCUS_RED_BORDER}`,
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <Typography
            style={{ color: EPOCUS_RED, fontWeight: 600, fontSize: rem(13) }}
          >
            <AlertTriangle
              width={15}
              height={15}
              fill="currentColor"
              style={{ verticalAlign: "text-bottom", marginRight: 4 }}
            />
            Images captured in <strong>{conflictingPreset}</strong> preset —
            this doesn&apos;t match the{" "}
            <strong>{EXAM_LABELS[exam.examType]}</strong> exam type. Check you
            have the right study, or add a different exam.
          </Typography>
        </Box>
      )}
      <EpocusImageGallery
        images={hydratedImages}
        selectedIndex={galleryIndex}
        onImageClick={setGalleryIndex}
      />
    </>
  );

  const shownData = viewedVersion?.worksheet ?? currentData;
  const shownReadOnly = readOnly || !!viewedVersion;

  const worksheetProps = {
    isAllNormal,
    onToggleAllNormal: toggleAllNormal,
    readOnly: shownReadOnly,
    imageGallery,
  };

  const ExamIcon = exam ? EXAM_ICONS[exam.examType] : null;

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusEncounter(encounterId ?? ""))}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back to encounter
      </Button>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Box
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              backgroundColor: EPOCUS_SELECTED_BG,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-hidden
          >
            {ExamIcon && (
              <ExamIcon width={28} height={28} fill={EPOCUS_ICON_ACTIVE} />
            )}
          </Box>
          <Box>
            <Typography
              variant="h5"
              style={{
                fontWeight: 700,
                color: EPOCUS_TEXT_PRIMARY,
                fontSize: rem(20),
              }}
            >
              {EXAM_LABELS[exam.examType]} Worksheet
            </Typography>
            <Typography
              variant="body2"
              style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(13) }}
            >
              {isSubmitted ? "Submitted" : "Draft"}
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          style={{ gap: 6 }}
        >
          {!readOnly && (
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Auto-saved
            </Typography>
          )}
        </Box>
      </Box>

      {signedReview && <ReviewOutcomeCard review={signedReview} />}

      {billingCodesEnabled && exam.signedAt && exam.amendments.length > 0 && (
        <ReportHistory
          signedAt={exam.signedAt}
          amendments={exam.amendments}
          versions={amendMode ? [] : versions}
          viewing={viewedVersion?.key ?? "current"}
          onView={setViewingVersion}
        />
      )}

      {shownData.examType === "cardiac" && (
        <CardiacWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "efast" && (
        <EfastWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "lung" && (
        <LungWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "bladder" && (
        <BladderWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "renal" && (
        <RenalWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "aorta" && (
        <AortaWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "pregnancy" && (
        <PregnancyWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {shownData.examType === "msk" && (
        <MskWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          readOnly={shownReadOnly}
          images={hydratedImages}
          onTagImages={handleTagMskImages}
          regionCodes={mskRegionCodes}
          onOpenGuidedProcedure={
            !isSubmitted && enabledExamTypes.includes("guided_joint_injection")
              ? setGuidedSwitchRegion
              : undefined
          }
        />
      )}
      {isProcedureWorksheet(shownData) && (
        <ProcedureWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          images={hydratedImages}
          onToggleNeedleInTarget={handleToggleNeedleInTarget}
          readOnly={shownReadOnly}
          imageGallery={imageGallery}
        />
      )}

      {isUsWorksheet(shownData) && (
        <UsWorksheet
          data={shownData}
          onChange={handleWorksheetChange}
          readOnly={shownReadOnly}
          imageGallery={imageGallery}
        />
      )}

      {billingCodesEnabled && !(isSubmitted && !existingBilling) && (
        <BillingSection
          lines={isSubmitted && existingBilling ? signedLines : billingLines}
          otherLines={readOnly ? [] : otherLines}
          ticked={
            isSubmitted && existingBilling
              ? Object.fromEntries(signedLines.map((line) => [line.key, true]))
              : tickedByKey
          }
          onToggle={(line, ticked) =>
            setTickState({
              scope: tickScope,
              overrides: { ...tickOverrides, [line.key]: ticked },
            })
          }
          modifierFor={modifierFor}
          readOnly={readOnly}
          notSetUpMessage={program ? null : BILLING_NOT_SET_UP}
        />
      )}

      {billingCodesEnabled && (
        <NonBillableControl
          reason={exam.nonBillableReason}
          onChange={handleNonBillableChange}
          disabled={
            !canEditExam ||
            lockedToOtherPerformer ||
            isSubmitting ||
            (!!exam.signedAt && exam.signedByUserId !== user.id) ||
            billingStatus?.status === "EXPORTED"
          }
          lockedMessage={
            billingStatus?.status === "EXPORTED" ? NON_BILLABLE_EXPORTED : null
          }
        />
      )}

      {billingCodesEnabled && isSubmitted && billingStatus && (
        <BillingStatusNote status={billingStatus} />
      )}

      {canAmend && !viewedVersion && (
        <AmendReportPanel
          key={exam.amendments.length}
          mode={amendMode}
          canConfirm={billingStatus?.holdReason === "MAJOR_DISCREPANCY"}
          submitting={isSubmitting}
          onStart={handleStartAmendment}
          onCancel={closeAmendment}
          onSubmit={handleSignAmendment}
        />
      )}

      <SwitchToGuidedProcedureDialog
        open={guidedSwitchRegion !== null}
        switching={switchingWorksheet}
        onClose={() => setGuidedSwitchRegion(null)}
        onConfirm={handleSwitchToGuidedJointInjection}
      />

      {lockedToOtherPerformer && (
        <PerformerLockNotice>
          This scan has been claimed by another performer. You can read the
          worksheet, but only they can complete it.
        </PerformerLockNotice>
      )}

      <RoleGate can="editOwnExam">
        <SigningChecklist missing={signingGaps} />
        <WorksheetActions
          isComplete={
            billingCodesEnabled ? signingGaps.length === 0 : isComplete
          }
          onSaveDraft={handleSaveDraft}
          onSubmit={handleSubmit}
          isSubmitted={isSubmitted}
          submitLabel={billingCodesEnabled ? "Sign" : undefined}
          submittedLabel={exam.signedAt ? "Signed" : undefined}
          disabled={
            !!conflictingPreset ||
            isSubmitting ||
            lockedToOtherPerformer ||
            untaggedMskImageCount > 0
          }
        />
      </RoleGate>
    </PageRoot>
  );
};
